/**
 * What is on each shelf, in packs, with a value — and which items are low.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';
import { packs } from './ItemPicker.jsx';

export function StockTab({ data, canEdit }) {
  const [term, setTerm] = useState('');
  const [category, setCategory] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  // Both shelves side by side, or one of them on its own — the kitchen's stock is a question of its own.
  const [shelf, setShelf] = useState('both');

  const rows = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return (data?.items ?? []).filter(
      (i) =>
        (!needle || i.name.toLowerCase().includes(needle)) &&
        (!category || i.category === category) &&
        (!onlyLow || i.low) &&
        (shelf === 'both' || (shelf === 'store' ? i.store.packs !== 0 : i.kitchen.packs !== 0))
    );
  }, [data, term, category, onlyLow, shelf]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div role="tablist" className="flex rounded-lg border border-rule p-0.5">
          {[
            ['both', 'Both shelves'],
            ['store', 'Store'],
            ['kitchen', 'Kitchen'],
          ].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={shelf === id} onClick={() => setShelf(id)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${shelf === id ? 'bg-brand-500 text-black' : 'text-ink-2'}`}>
              {label}
            </button>
          ))}
        </div>
        <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search items" aria-label="Search items" className="h-10 w-56 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" className="h-10 rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink">
          <option value="">All categories</option>
          {(data?.categories ?? []).map((c) => (
            <option key={c.category} value={c.category}>
              {c.category} ({c.items})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <input type="checkbox" checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} />
          Low only {data?.totals?.low ? `(${data.totals.low})` : ''}
        </label>
        <div className="ml-auto flex items-center gap-4 text-sm text-ink-2">
          <span>
            In the store <span className="font-medium text-ink">{formatPKR(data?.totals?.storeValue ?? 0)}</span>
          </span>
          <span>
            In the kitchen <span className="font-medium text-ink">{formatPKR(data?.totals?.kitchenValue ?? 0)}</span>
          </span>
          <Button size="sm" variant="secondary" onClick={() => window.print()}>
            Print
          </Button>
          {canEdit && (
            <Button size="sm" onClick={() => setAdding(true)}>
              Add item
            </Button>
          )}
        </div>
      </div>

      <StockSheet rows={rows} shelf={shelf} category={category} onlyLow={onlyLow} term={term} />

      <div className="overflow-x-auto rounded-xl border border-rule">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-3">
              <th className="p-3 font-medium">Item</th>
              <th className="p-3 font-medium">Pack</th>
              <th className={`p-3 text-right font-medium ${shelf === 'store' ? 'text-brand-300' : ''}`}>In store (packs)</th>
              <th className={`p-3 text-right font-medium ${shelf === 'kitchen' ? 'text-brand-300' : ''}`}>In kitchen (packs)</th>
              <th className="p-3 text-right font-medium">Last price / pack</th>
              <th className="p-3 text-right font-medium">Avg cost / pack</th>
              <th className="p-3 text-right font-medium">Value</th>
              {canEdit && <th className="p-3" aria-label="Edit" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="border-t border-rule">
                <td className="p-3 text-ink">
                  {i.name}
                  {i.low && <span className="ml-2 rounded-full bg-warn/15 px-1.5 text-xs text-warn">low</span>}
                  <div className="text-xs text-ink-3">{i.category}</div>
                </td>
                <td className="p-3 text-ink-2">
                  {i.packName}
                  {i.packSize !== 1 && (
                    <span className="text-ink-3">
                      {' '}
                      = {packs(i.packSize)} {i.unit}
                    </span>
                  )}
                </td>
                <td className={`p-3 text-right tabular-nums ${i.store.packs < 0 ? 'text-bad' : 'text-ink'}`}>{packs(i.store.packs)}</td>
                <td className={`p-3 text-right tabular-nums ${i.kitchen.packs < 0 ? 'text-bad' : 'text-ink'}`}>{packs(i.kitchen.packs)}</td>
                <td className="p-3 text-right tabular-nums text-ink-2">{i.packPrice ? formatPKR(i.packPrice) : '—'}</td>
                <td className="p-3 text-right tabular-nums text-ink-2">{i.store.packs || i.kitchen.packs ? formatPKR(i.avgPackPrice) : '—'}</td>
                <td className="p-3 text-right tabular-nums text-ink-2">{formatPKR(i.store.value + i.kitchen.value)}</td>
                {canEdit && (
                  <td className="p-3 text-right">
                    <button type="button" onClick={() => setEditing(i)} className="text-xs font-medium text-brand-300 hover:underline">
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-sm text-ink-3">
                  No items match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && <EditItemSheet item={editing} onClose={() => setEditing(null)} />}
      {adding && <AddItemSheet units={data?.units ?? []} categories={data?.categories ?? []} onClose={() => setAdding(false)} />}
    </div>
  );
}

/** The pack an item comes in, what it last cost, and when to call it low. */
function EditItemSheet({ item, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ packName: item.packName, packSize: item.packSize, packPrice: item.packPrice ?? '', lowPacks: item.lowPacks });
  const save = useMutation({
    mutationFn: () => api.put(`/store/items/${item.id}`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['store'] });
      onClose();
    },
  });
  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.delete(`/store/items/${item.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['store'] });
      onClose();
    },
  });
  const held = item.store.packs !== 0 || item.kitchen.packs !== 0;
  return (
    <div role="dialog" aria-modal="true" aria-label={`Edit ${item.name}`} className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <form
        className="w-full max-w-sm rounded-2xl bg-surface p-5"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <h2 className="mb-1 text-lg font-semibold text-ink">{item.name}</h2>
        <p className="mb-4 text-xs text-ink-3">Stock is kept in {item.unit}. The pack is what you buy and hand over.</p>
        <div className="space-y-3">
          <Field label="Pack" value={form.packName} onChange={(e) => setForm({ ...form, packName: e.target.value })} placeholder="e.g. 4 kg tub" />
          <Field label={`${item.unit} in one pack`} type="number" step="any" min="0.001" value={form.packSize} onChange={(e) => setForm({ ...form, packSize: e.target.value })} />
          <Field label="Price per pack (Rs)" type="number" step="any" min="0" value={form.packPrice} onChange={(e) => setForm({ ...form, packPrice: e.target.value })} />
          <Field label="Low when store has (packs)" type="number" step="any" min="0" value={form.lowPacks} onChange={(e) => setForm({ ...form, lowPacks: e.target.value })} />
        </div>
        {(save.error || remove.error) && (
          <p role="alert" className="mt-3 text-sm text-bad">
            {save.error?.message || remove.error?.message}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" className="flex-1" busy={save.isPending}>
            Save
          </Button>
        </div>
        {/* Taking the item off the list. Refused while anything of it is on a shelf. */}
        <div className="mt-4 border-t border-rule pt-3">
          {!confirmRemove ? (
            <button type="button" onClick={() => setConfirmRemove(true)} className="text-sm text-bad hover:underline">
              Remove this item from the list
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-ink-2">
                {held ? 'It still has stock on a shelf. Issue it, use it up or count it to zero at an audit first.' : 'It stops appearing on the store screens. Its slips and counts are kept.'}
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setConfirmRemove(false)}>
                  Keep it
                </Button>
                <Button type="button" variant="danger" size="sm" disabled={held} busy={remove.isPending} onClick={() => remove.mutate()}>
                  Remove
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

/** A new item on the list: its name, the small unit it is counted in, the pack it comes in. */
function AddItemSheet({ units, categories, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', category: '', unit: 'pc', packName: '', packSize: 1, packPrice: '', lowPacks: 5 });
  const save = useMutation({
    mutationFn: () => api.post('/store/items', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['store'] });
      onClose();
    },
  });
  const unitLabel = { pc: 'pieces', g: 'grams', kg: 'kilograms', ml: 'millilitres', L: 'litres' };
  const offered = units.filter((u) => ['pc', 'g', 'kg', 'ml', 'L'].includes(u.abbreviation));
  return (
    <div role="dialog" aria-modal="true" aria-label="Add an item" className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <form
        className="w-full max-w-md rounded-2xl bg-surface p-5"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <h2 className="mb-1 text-lg font-semibold text-ink">Add an item</h2>
        <p className="mb-4 text-xs text-ink-3">Count whole things in pieces. For anything a half pack of is worth counting, use grams or millilitres and say how many are in a pack.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" className="sm:col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus required />
          <label className="text-sm font-medium text-ink-2">
            Category
            <input list="store-categories" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm font-normal text-ink" />
            <datalist id="store-categories">
              {categories.map((c) => (
                <option key={c.category} value={c.category} />
              ))}
            </datalist>
          </label>
          <label className="text-sm font-medium text-ink-2">
            Counted in
            <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1.5 h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm font-normal text-ink">
              {offered.map((u) => (
                <option key={u.id} value={u.abbreviation}>
                  {unitLabel[u.abbreviation] || u.name}
                </option>
              ))}
            </select>
          </label>
          <Field label="Pack" placeholder="e.g. jar, 4 kg tub, pack of 12" value={form.packName} onChange={(e) => setForm({ ...form, packName: e.target.value })} />
          <Field label={`${form.unit} in one pack`} type="number" step="any" min="0.001" value={form.packSize} onChange={(e) => setForm({ ...form, packSize: e.target.value })} />
          <Field label="Price per pack (Rs)" type="number" step="any" min="0" value={form.packPrice} onChange={(e) => setForm({ ...form, packPrice: e.target.value })} />
          <Field label="Low when store has (packs)" type="number" step="any" min="0" value={form.lowPacks} onChange={(e) => setForm({ ...form, lowPacks: e.target.value })} />
        </div>
        {save.error && (
          <p role="alert" className="mt-3 text-sm text-bad">
            {save.error.message}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" className="flex-1" busy={save.isPending}>
            Add
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * The stock list on paper: what is on screen, with a blank box per item to
 * count into. Hidden on screen; the only thing on the page when printed.
 */
function StockSheet({ rows, shelf, category, onlyLow, term }) {
  const when = new Date().toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const scope = [
    shelf === 'both' ? 'Both shelves' : shelf === 'store' ? 'Store shelf' : 'Kitchen shelf',
    category || 'all categories',
    onlyLow ? 'low items only' : null,
    term.trim() ? `matching "${term.trim()}"` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const total = rows.reduce((sum, i) => sum + i.store.value + i.kitchen.value, 0);
  const showStore = shelf !== 'kitchen';
  const showKitchen = shelf !== 'store';
  return (
    <div className="statement print-only">
      <h2 className="text-lg font-semibold">Stock sheet</h2>
      <p className="mb-3 text-xs">
        {scope} · {rows.length} items · printed {when}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left">
            <th className="border-b p-1 font-medium">Item</th>
            <th className="border-b p-1 font-medium">Pack</th>
            {showStore && <th className="border-b p-1 text-right font-medium">Store (packs)</th>}
            {showKitchen && <th className="border-b p-1 text-right font-medium">Kitchen (packs)</th>}
            <th className="border-b p-1 text-right font-medium">Avg / pack</th>
            <th className="border-b p-1 text-right font-medium">Value</th>
            {showStore && <th className="border-b p-1 text-right font-medium" style={{ width: 70 }}>Counted store</th>}
            {showKitchen && <th className="border-b p-1 text-right font-medium" style={{ width: 70 }}>Counted kitchen</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i.id}>
              <td className="border-b p-1">
                {i.name}
                <span className="text-xs"> · {i.category}</span>
              </td>
              <td className="border-b p-1">{i.packName || i.unit}</td>
              {showStore && <td className="border-b p-1 text-right tabular-nums">{packs(i.store.packs)}</td>}
              {showKitchen && <td className="border-b p-1 text-right tabular-nums">{packs(i.kitchen.packs)}</td>}
              <td className="border-b p-1 text-right tabular-nums">{i.store.packs || i.kitchen.packs ? formatPKR(i.avgPackPrice) : '—'}</td>
              <td className="border-b p-1 text-right tabular-nums">{formatPKR(i.store.value + i.kitchen.value)}</td>
              {showStore && <td className="border-b p-1" />}
              {showKitchen && <td className="border-b p-1" />}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold">
            <td className="p-1" colSpan={2 + (showStore ? 1 : 0) + (showKitchen ? 1 : 0) + 1}>
              Total value of the items shown
            </td>
            <td className="p-1 text-right tabular-nums">{formatPKR(total)}</td>
            <td colSpan={(showStore ? 1 : 0) + (showKitchen ? 1 : 0)} />
          </tr>
        </tfoot>
      </table>
      <p className="mt-2 text-xs">Quantities in packs at the moment of printing. Values at the average cost of the packs on each shelf.</p>
    </div>
  );
}
